import { Injectable, inject } from '@angular/core';
import { Router, NavigationError } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { AppState } from '../store/app.state';
import { UserActions } from '../store/user/user.actions';
import { AuthService } from './auth.service';
import { Logger } from './logger.service';

/** When the app last reloaded itself after a page's code failed to load (sessionStorage, epoch ms). */
export const CHUNK_RELOAD_KEY = 'compass.chunkReloadAt';
/** A second failure within this time of that reload shows the error page instead of reloading again. */
export const RELOAD_GUARD_MS = 30_000;
/** A page still here this long after its reload began didn't unload (e.g. the user cancelled the "Leave site?"
 *  prompt for unsaved changes): it handles navigation errors again. */
export const RELOAD_SETTLE_MS = 3_000;
/** Where showErrorPage() goes. */
const ERROR_PAGE_PATH = '/error';

/**
 * A lazily loaded page's code could not be fetched: the message each browser gives for a failed dynamic import
 * (Chrome/Edge, Firefox, Safari), or a bundler's ChunkLoadError.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  return name === 'ChunkLoadError'
    || (typeof message === 'string'
      && /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(message));
}

@Injectable({
  providedIn: 'root',
})
export class RoutingErrorHandlerService {
  private router = inject(Router);
  private store = inject(Store<AppState>);
  private authService = inject(AuthService);
  private readonly logger = inject(Logger);
  /** A reload is on its way: the page is about to go, so its further navigation failures are ignored. */
  private reloading = false;

  /**
   * Initialize routing error handler
   * Subscribes to router events and handles navigation errors
   */
  init(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationError => event instanceof NavigationError
        )
      )
      .subscribe((event: NavigationError) => {
        this.handleRoutingError(event);
      });
  }

  /**
   * Handle routing errors
   * - Clears session for auth-related errors
   * - Shows error page with logout option for all not-found routes
   */
  private handleRoutingError(event: NavigationError): void {
    const url = this.sanitizePath(event.url);
    const error = event.error;

    if (this.reloading) return;

    // The page's code is gone: this tab was opened before a deploy, which renamed every code file. A full reload
    // fetches the new app and opens the page (once: see reloadOnce).
    if (isChunkLoadError(error) && this.reloadOnce(event.url)) {
      this.logger.warn('Page code failed to load (a newer version was deployed); reloading once:', { url });
      return;
    }

    this.logger.error('Routing error:', {
      url,
      error,
      message: error?.message,
    });

    // The error page itself failed to open (it is in the main bundle, so not for want of its code; a guard or
    // resolver added later could): going to it again would loop.
    if (url === ERROR_PAGE_PATH) return;

    // Still no code after the reload (or no reload possible): a connection or server problem, not a wrong address
    // or account, so the error page offers to try again instead of logging out.
    if (isChunkLoadError(error)) {
      this.showErrorPage(url, 'load-failed');
      return;
    }

    // Check if it's an auth-related error - clear session immediately
    if (this.isAuthError(error)) {
      this.logger.debug('Auth error detected, clearing session...');
      this.handleAuthError(url);
      return;
    }

    // For all route not found errors (including admin, protected, and any invalid routes)
    // Show error page with logout option
    const errorType = this.isAdminRoute(url) ? 'unauthorized' : 'not-found';
    const isProtected = this.isProtectedRoute(url);

    this.logger.debug(`Route not found: ${url}, showing error page with logout option...`);
    this.showErrorPage(url, errorType, true, isProtected);
  }

  /**
   * Reloads the whole app at `url`, unless it already did so within RELOAD_GUARD_MS (the new code failed too:
   * a network or server problem, not a deploy). The marker stays for that time, so every further failure in it
   * goes to the error page. Without session storage there is no guard, so no reload either.
   * Returns whether it reloads.
   */
  private reloadOnce(url: string): boolean {
    try {
      const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
      const elapsed = Date.now() - last;
      // A clock set back since then counts as outside the guard time.
      if (last && elapsed >= 0 && elapsed < RELOAD_GUARD_MS) return false;
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
      return false;
    }
    this.reloading = true;
    this.reloadPage(url);
    // Normally the page unloads first. Still here, the reload didn't happen: later errors are handled again (the
    // guard marker stays, so within RELOAD_GUARD_MS the next one shows the error page instead of reloading).
    setTimeout(() => (this.reloading = false), RELOAD_SETTLE_MS);
    return true;
  }

  /** A full page load of `url` (an in-app URL). Its own method, so specs can stand in for it. */
  protected reloadPage(url: string): void {
    window.location.assign(url);
  }

  /**
   * Strip query string and fragment from a URL so stored/logged paths
   * never leak query params (tokens, PII, returnUrl chains, etc.).
   */
  private sanitizePath(url: string): string {
    try {
      const u = new URL(url, window.location.origin);
      return u.pathname;
    } catch {
      return url.split('?')[0].split('#')[0];
    }
  }

  /**
   * Check if the error is auth-related
   */
  private isAuthError(error: any): boolean {
    if (!error) return false;

    const errorMessage = error.message?.toLowerCase() || '';
    const errorName = error.name?.toLowerCase() || '';

    return (
      errorMessage.includes('auth') ||
      errorMessage.includes('unauthorized') ||
      errorMessage.includes('forbidden') ||
      errorMessage.includes('token') ||
      errorName.includes('auth') ||
      error.status === 401 ||
      error.status === 403
    );
  }

  /**
   * Check if the route is protected (requires authentication)
   */
  private isProtectedRoute(url: string): boolean {
    // Routes that require authentication
    const protectedPaths = ['/client', '/compliance', '/manager', '/company', '/admin'];
    return protectedPaths.some((path) => url.startsWith(path));
  }

  /**
   * Check if the route is an admin route
   */
  private isAdminRoute(url: string): boolean {
    return url.startsWith('/admin');
  }

  /**
   * Handle auth-related errors
   * Clears session and redirects to login
   */
  private handleAuthError(attemptedUrl: string): void {
    // Clear user state in store
    this.store.dispatch(UserActions.logoutSuccess());

    // Only clear the local browser session here. A full Keycloak logout would
    // discard the attempted route and force a hard redirect, which makes it
    // much harder to return the user to the page they were on after re-login.
    this.authService.clearSession();

    this.router.navigate(['/auth/login'], {
      queryParams: {
        returnUrl: attemptedUrl,
        error: 'session-expired',
      },
    });
  }

  /**
   * Show error page with details
   */
  private showErrorPage(
    attemptedPath: string,
    errorType: 'not-found' | 'unauthorized' | 'forbidden' | 'load-failed' = 'not-found',
    requiresLogout: boolean = false,
    isProtectedRoute: boolean = false
  ): void {
    this.router.navigate([ERROR_PAGE_PATH], {
      queryParams: {
        path: attemptedPath,
        type: errorType,
        logout: requiresLogout ? 'true' : undefined,
        protected: isProtectedRoute ? 'true' : undefined,
      },
      skipLocationChange: false,
    });
  }
}
